export type PortalElementAlignment = {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
};

type PortalElementPosition = {
    alignment: PortalElementAlignment;
    left: number;
    top: number;
    arrowShiftX?: number;
};

type HorizontalAlignment = {
    arrowShiftX?: number;
    left: number;
};

export const MIN_DISTANCE_FROM_EDGE = 12;

const calculateTooltipHorizontalPosition = (
    triggerRect: DOMRect,
    portalElementRect: DOMRect,
    minDistanceFromEdge: number,
    arrowRelativeElement: HTMLElement | null = null
) => {
    const arrowRelativeElementRect = arrowRelativeElement ? arrowRelativeElement.getBoundingClientRect() : triggerRect;

    const triggerCenter = triggerRect.left + triggerRect.width / 2;
    const arrowRelativeElementCenter = arrowRelativeElementRect.left + arrowRelativeElementRect.width / 2;

    const distanceFromLeftEdge = triggerCenter - portalElementRect.width / 2;
    const distanceFromLeftEdgeArrow = arrowRelativeElementCenter - portalElementRect.width / 2;

    let left = distanceFromLeftEdge;
    let arrowShiftX = 0;

    if (distanceFromLeftEdge < minDistanceFromEdge) {
        arrowShiftX = minDistanceFromEdge - distanceFromLeftEdgeArrow;
        left += minDistanceFromEdge - distanceFromLeftEdge;
    }

    const portalElementRightEdge = triggerCenter + portalElementRect.width / 2;
    const portalElementRightEdgeArrow = arrowRelativeElementCenter + portalElementRect.width / 2;

    if (portalElementRightEdge > window.innerWidth - minDistanceFromEdge) {
        arrowShiftX = window.innerWidth - minDistanceFromEdge * 2 - portalElementRightEdgeArrow;
        left += window.innerWidth - minDistanceFromEdge * 2 - portalElementRightEdge;
    }

    return {
        arrowShiftX,
        left,
    };
};

const calculateDropdownHorizontalPosition = (
    triggerRect: DOMRect,
    portalElementRect: DOMRect,
    minDistanceFromEdge: number,
    alignment: PortalElementAlignment
) => {
    let left = 0;

    if (alignment.horizontal === 'left') {
        left = triggerRect.left;

        if (triggerRect.left + portalElementRect.width > window.innerWidth - minDistanceFromEdge) {
            left = triggerRect.right - portalElementRect.width;
        }
    }

    if (alignment.horizontal === 'right') {
        left = triggerRect.right - portalElementRect.width;

        if (left < 0) {
            left = triggerRect.left;
        }
    }

    return {
        left,
    };
};

const calculatePortalElementPosition = (
    triggerElement: HTMLElement,
    portalElement: HTMLElement,
    preferredAlignment: PortalElementAlignment,
    minDistanceFromEdge: number = MIN_DISTANCE_FROM_EDGE,
    forcedAlignment: Boolean = false,
    arrowRelativeElement: HTMLElement | null = null,
    fixed: boolean = false
): PortalElementPosition => {
    const triggerRect = triggerElement.getBoundingClientRect();
    const portalElementRect = portalElement.getBoundingClientRect();
    const alignment = { ...preferredAlignment };

    if (!forcedAlignment) {
        if (triggerRect.top - portalElementRect.height < minDistanceFromEdge) {
            alignment.vertical = 'bottom';
        }

        if (triggerRect.top + portalElementRect.height > window.innerHeight - minDistanceFromEdge) {
            alignment.vertical = 'top';
        }
    }

    const top =
        alignment.vertical === 'top'
            ? (fixed ? 0 : window.scrollY) + triggerRect.top - portalElementRect.height
            : (fixed ? 0 : window.scrollY) + triggerRect.top + triggerRect.height;

    const horizontalAlignment: HorizontalAlignment =
        alignment.horizontal === 'center'
            ? calculateTooltipHorizontalPosition(
                  triggerRect,
                  portalElementRect,
                  minDistanceFromEdge,
                  arrowRelativeElement
              )
            : calculateDropdownHorizontalPosition(triggerRect, portalElementRect, minDistanceFromEdge, alignment);

    return {
        left: horizontalAlignment.left,
        top,
        arrowShiftX: horizontalAlignment.arrowShiftX || 0,
        alignment,
    };
};

export default calculatePortalElementPosition;
